import React from 'react';
import PropTypes from 'prop-types';

import Note from '../../note';
import formFieldOperationOptions from '../../utils/formFieldOperationOptions';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import getFormattedPricing from '../../iot/orderDetailsSection/utils';
import { FieldLabelWrapper } from './css';
import FieldGuide from './common/fieldGuide';
import FieldTitle from './common/FieldTitle';

const FieldSelect = ({ data }) => {
  const {
    ref,
    name,
    label,
    value,
    required,
    options,
    onChange,
    note,
    tooltip,
    tooltipLong,
    toolTipRichText,
    disabled,
    readonly,
    noteAppearsBelowField,
    hideEmptyValue
  } = data;
  const renderNotes = () => {
    return (
      note &&
      note.content && (
        <Note
          type="richText"
          options={formFieldOperationOptions(value)}
          data={note}
        />
      )
    );
  };

  const localizedStrings = useLocalizedStringsContext();
  const { quoteForm } = localizedStrings;
  const tooltipMessage = tooltip || tooltipLong || toolTipRichText;
  return (
    <div
      className={
        ['outlet', 'channelId'].includes(name)
          ? 'outlet-field-container'
          : 'select-field-container'
      }
    >
      <FieldLabelWrapper>
        <FieldTitle required={required} label={label} />
        {tooltipMessage && (
          <FieldGuide tooltip={tooltipMessage} isRichText={toolTipRichText} />
        )}
      </FieldLabelWrapper>
      {!noteAppearsBelowField && renderNotes()}
      <select
        ref={ref}
        id={name}
        name={name}
        onChange={onChange}
        disabled={disabled || readonly}
        className="down-arrow"
      >
        {!hideEmptyValue && <option key="option" value="" />}
        {options &&
          options.map(item => {
            if (!item.fields) return null;

            const {
              fields: {
                value: itemValue,
                label: itemLabel,
                price,
                monthlyPayment,
                monthlyPayment12,
                monthlyPayment24,
                monthlyPayment36,
                showPricesInLabel,
                isVisible
              }
            } = item;

            // Hide option if isVisible is set to No
            if (typeof isVisible !== 'undefined' && !isVisible) return null;

            const key = `${name}option${itemValue}`;
            let optionLabel = itemLabel;
            const priceLabelArr = [];
            if (showPricesInLabel) {
              if (price)
                priceLabelArr.push(
                  `${quoteForm.upfront}: ${getFormattedPricing(price)}`
                );
              if (monthlyPayment !== undefined)
                priceLabelArr.push(
                  `${quoteForm.monthly}: ${getFormattedPricing(monthlyPayment)}`
                );
              if (monthlyPayment12 !== undefined)
                priceLabelArr.push(
                  `${quoteForm.month12}: ${getFormattedPricing(
                    monthlyPayment12
                  )}`
                );
              if (monthlyPayment24 !== undefined)
                priceLabelArr.push(
                  `${quoteForm.month24}: ${getFormattedPricing(
                    monthlyPayment24
                  )}`
                );
              if (monthlyPayment36 !== undefined)
                priceLabelArr.push(
                  `${quoteForm.month36}: ${getFormattedPricing(
                    monthlyPayment36
                  )}`
                );
              if (priceLabelArr.length)
                optionLabel += `:   (${priceLabelArr.join(', ')})`;
            }

            return (
              <option
                key={key}
                value={itemValue}
                selected={value === itemValue}
              >
                {optionLabel}
              </option>
            );
          })}
      </select>
      {noteAppearsBelowField && renderNotes()}
    </div>
  );
};

FieldSelect.propTypes = {
  data: PropTypes.shape({
    ref: PropTypes.object,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    required: PropTypes.bool,
    readonly: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          value: PropTypes.string.isRequired
        })
      })
    ),
    onChange: PropTypes.func.isRequired,
    note: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    disabled: PropTypes.bool,
    tooltip: PropTypes.string,
    tooltipLong: PropTypes.string,
    toolTipRichText: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    noteAppearsBelowField: PropTypes.bool,
    hideEmptyValue: PropTypes.bool
  }).isRequired
};

export default FieldSelect;
